/**
 * Owner Dashboard Service
 * Dashboard V3를 위한 API 서비스
 */
import { API_BASE_URL } from '../../../config/api';
import { RevenueStats } from '../models/RevenueStats';
import { StudentAlerts } from '../models/StudentAlerts';
import { SystemHealth } from '../models/SystemHealth';

// nginx 프록시 사용 시 (API_BASE_URL이 빈 문자열) /api 경로 추가
const getApiUrl = (path: string) => {
  const baseUrl = API_BASE_URL === '' ? '/api' : API_BASE_URL;
  return `${baseUrl}${path}`;
};

const API_ENDPOINTS = {
  revenueStats: getApiUrl('/v1/owner/dashboard/revenue-stats'),
  studentAlerts: getApiUrl('/v1/owner/dashboard/student-alerts'),
  systemHealth: getApiUrl('/v1/owner/dashboard/system-health'),
  seatSummary: getApiUrl('/v1/owner/dashboard/seat-summary'),
  sendNotification: getApiUrl('/v1/owner/dashboard/send-notification'),
};

/**
 * 인증 헤더 생성
 */
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

/**
 * API 응답 타입
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * 매출 통계 조회
 */
export async function fetchRevenueStats(): Promise<RevenueStats> {
  try {
    const response = await fetch(API_ENDPOINTS.revenueStats, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result: ApiResponse<RevenueStats> = await response.json();

    if (!result.success) {
      throw new Error(result.message || '매출 통계 조회 실패');
    }

    return result.data;
  } catch (error) {
    console.error('❌ 매출 통계 조회 오류:', error);
    throw error;
  }
}

/**
 * 학생 알림 조회
 */
export async function fetchStudentAlerts(): Promise<StudentAlerts> {
  try {
    const response = await fetch(API_ENDPOINTS.studentAlerts, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result: ApiResponse<StudentAlerts> = await response.json();

    if (!result.success) {
      throw new Error(result.message || '학생 알림 조회 실패');
    }

    return result.data;
  } catch (error) {
    console.error('❌ 학생 알림 조회 오류:', error);
    throw error;
  }
}

/**
 * 시스템 헬스 조회
 */
export async function fetchSystemHealth(): Promise<SystemHealth> {
  try {
    const response = await fetch(API_ENDPOINTS.systemHealth, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result: ApiResponse<SystemHealth> = await response.json();

    if (!result.success) {
      throw new Error(result.message || '시스템 헬스 조회 실패');
    }

    return result.data;
  } catch (error) {
    console.error('❌ 시스템 헬스 조회 오류:', error);
    throw error;
  }
}

/**
 * 좌석 현황 조회
 */
export interface SeatSummary {
  total: number;
  occupied: number;
  available: number;
  maintenance: number;
  broken: number;
  alerts: SeatAlert[];
}

export interface SeatAlert {
  type: 'long_away' | 'broken' | 'dirty';
  seat_number: string;
  duration?: string;
  user_name?: string;
  message: string;
}

export async function fetchSeatSummary(): Promise<SeatSummary> {
  try {
    const response = await fetch(API_ENDPOINTS.seatSummary, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result: ApiResponse<SeatSummary> = await response.json();

    if (!result.success) {
      throw new Error(result.message || '좌석 현황 조회 실패');
    }

    return result.data;
  } catch (error) {
    console.error('❌ 좌석 현황 조회 오류:', error);
    throw error;
  }
}

/**
 * 알림 발송 요청 타입
 */
export interface SendNotificationRequest {
  user_id: string;
  notification_type: 'expiring_soon' | 'unpaid' | 'long_absent' | 'welcome';
  message_type: 'sms' | 'kakao';
}

/**
 * 알림 발송 응답 타입
 */
interface SendNotificationResponse {
  success: boolean;
  message?: string;
  provider: string;
  sent_at: string;
}

/**
 * 알림 발송 공통 함수
 */
async function sendNotification(
  userId: string,
  notificationType: 'expiring_soon' | 'unpaid' | 'long_absent' | 'welcome',
  messageType: 'sms' | 'kakao'
): Promise<boolean> {
  try {
    const response = await fetch(
      `${API_ENDPOINTS.sendNotification}?user_id=${userId}&notification_type=${notificationType}&message_type=${messageType}`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result: ApiResponse<SendNotificationResponse> = await response.json();

    if (!result.success) {
      throw new Error(result.message || '알림 발송 실패');
    }

    return true;
  } catch (error) {
    console.error(`❌ ${messageType.toUpperCase()} 발송 오류:`, error);
    throw error;
  }
}

/**
 * SMS 발송
 */
export async function sendSMS(
  userId: string,
  notificationType: 'expiring_soon' | 'unpaid' | 'long_absent'
): Promise<boolean> {
  return sendNotification(userId, notificationType, 'sms');
}

/**
 * 카카오톡 발송
 */
export async function sendKakao(
  userId: string,
  notificationType: 'expiring_soon' | 'unpaid' | 'long_absent'
): Promise<boolean> {
  return sendNotification(userId, notificationType, 'kakao');
}

/**
 * 회원 삭제
 */
export async function deleteMember(memberId: string): Promise<void> {
  try {
    const response = await fetch(getApiUrl(`/v1/owner/members/${memberId}`), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
  } catch (error) {
    console.error('❌ 회원 삭제 오류:', error);
    throw error;
  }
}
